import { useEffect } from 'react';

import { expVal } from '@atlassian/jira-feature-experiments';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useShouldShowRovoButton } from './useShouldShowRovoButton.tsx';
import { useIsGlobalRovoButtonAvailableForRoute } from './useIsGlobalRovoButtonAvailableForRoute.tsx';
import { useIsRovoOnboardingTargetRoute } from './useIsRovoOnboardingTargetRoute.tsx';

/**
 * Returns whether the `jfind-rovo-for-jira-onboarding` experiment is enabled for the
 * current user, and fires a `feature exposed` Track event exactly once per mount when
 * the tenant is confirmed to be in their first two weeks.
 *
 * Delegates to `useShouldShowRovoButton` and `useIsGlobalRovoButtonAvailableForRoute`
 * to ensure only users who can see the global Rovo button are enrolled in the experiment.
 *
 * Should be wrapped with `functionWithCondition` at the call site to gate on
 * `jfind-rovo-for-jira-onboarding-gate`.
 */
export const useIsRovoForJiraOnboardingEnabled = (isFirstTwoWeeks: boolean | null): boolean => {
	const shouldShowRovoButton = useShouldShowRovoButton();
	const isAvailableForRoute = useIsGlobalRovoButtonAvailableForRoute();
	const isRovoOnboardingTargetRoute = useIsRovoOnboardingTargetRoute();

	const isEnabled =
		isFirstTwoWeeks === true &&
		shouldShowRovoButton &&
		isAvailableForRoute &&
		isRovoOnboardingTargetRoute &&
		expVal('jfind-rovo-for-jira-onboarding', 'isEnabled', false);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (isEnabled) {
			fireTrackAnalytics(
				createAnalyticsEvent({ action: 'exposed', actionSubject: 'feature' }),
				'feature exposed',
				{ flagKey: 'jfind-rovo-for-jira-onboarding', isEnabled },
			);
		}
	}, [isEnabled, createAnalyticsEvent]);

	return isEnabled;
};
